import { useState, useEffect } from 'react';

export interface MonthlyRevenueRecord {
  year: number;
  month: number;
  invoice_month: string;
  global_invoiced_usd: number;
  global_invoice_count: number;
  global_unique_clients: number;
  currencies_used: number;
  prev_month_invoiced: number;
  mom_change_usd: number;
  mom_change_percent: number;
  prev_year_invoiced: number;
  yoy_change_usd: number;
  yoy_change_percent: number;
  three_month_avg: number;
  six_month_avg: number;
  six_month_stddev: number;
  mom_trend: string;
  yoy_trend: string;
  volatility_level: string;
  trend_position: string; // ABOVE_TREND | BELOW_TREND | AT_TREND | Normal
  top_3_currencies_details: string;
}

interface UseMonthlyRevenueResult {
  records: MonthlyRevenueRecord[];
  loading: boolean;
  error: string | null;
}

export function useMonthlyRevenue(): UseMonthlyRevenueResult {
  const [records, setRecords] = useState<MonthlyRevenueRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/json-data/global_invoiced_trends.json')
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((raw: any[]) => {
        const coerced: MonthlyRevenueRecord[] = raw.map(r => ({
          ...r,
          global_invoiced_usd: Number(r.global_invoiced_usd ?? 0),
          prev_month_invoiced: Number(r.prev_month_invoiced ?? 0),
          mom_change_usd: Number(r.mom_change_usd ?? 0),
          yoy_change_usd: Number(r.yoy_change_usd ?? 0),
          global_invoice_count: Number(r.global_invoice_count ?? 0),
          global_unique_clients: Number(r.global_unique_clients ?? 0),
        }));
        // Sort ascending by date for charts
        coerced.sort((a, b) => new Date(a.invoice_month).getTime() - new Date(b.invoice_month).getTime());
        setRecords(coerced);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return { records, loading, error };
}
